import { PutObjectCommand, type S3Client } from '@aws-sdk/client-s3'
import * as Sentry from '@sentry/react'
import {
  getJsonObject,
  paginateList,
  UPLOAD_BUCKET,
  withS3Client,
  type S3Io,
} from './s3'

export const VERSIONS_INDEX_FILENAME = 'versions-index.json'
export const INITIAL_TOY_VERSION = '1.0.0'

export type ToyVersionEntry = {
  version: string
  createdAt?: string
  [extra: string]: unknown
}

export type VersionsIndex = {
  schemaVersion: 1
  latest: string
  versions: ToyVersionEntry[]
}

export function versionsIndexKey(identityId: string, toyId: string): string {
  return `${identityId}/${toyId}/${VERSIONS_INDEX_FILENAME}`
}

export function isSemver(s: string): boolean {
  return /^\d+\.\d+\.\d+$/.test(s)
}

export function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] - pb[i]
  }
  return 0
}

// Highest well-formed semver in the list, or null when there is none.
export function maxSemver(versions: string[]): string | null {
  const valid = versions.filter(isSemver)
  if (valid.length === 0) return null
  return valid.reduce((a, b) => (compareSemver(a, b) >= 0 ? a : b))
}

// The version consumers should read for a toy. `latest` is denormalized and
// could drift from `versions[]` (hand edits, partial writes), so trust it only
// when it's well-formed and actually listed; otherwise fall back to the highest
// semver in `versions[]`.
export function latestVersionOf(index: VersionsIndex): string | null {
  const versions = index.versions.map((v) => v.version).filter(isSemver)
  if (isSemver(index.latest) && versions.includes(index.latest)) return index.latest
  return maxSemver(versions)
}

// The single definition of "which version is live" for a toy: what the
// versions-index says, else the highest version folder on disk. Every reader
// and mutator that cares about liveness must agree on this.
export function resolveLiveVersion(
  index: VersionsIndex | null,
  folders: string[],
): string | null {
  return (index ? latestVersionOf(index) : null) ?? maxSemver(folders)
}

export function buildInitialVersionsIndex(createdAt?: string): VersionsIndex {
  return {
    schemaVersion: 1,
    latest: INITIAL_TOY_VERSION,
    versions: [
      { version: INITIAL_TOY_VERSION, createdAt: createdAt ?? new Date().toISOString() },
    ],
  }
}

function parseVersionsIndex(obj: Record<string, unknown>): VersionsIndex | null {
  if (!Array.isArray(obj.versions)) return null
  const versions = obj.versions.filter(
    (v): v is ToyVersionEntry =>
      !!v && typeof v === 'object' && typeof (v as ToyVersionEntry).version === 'string',
  )
  return {
    schemaVersion: 1,
    latest: typeof obj.latest === 'string' ? obj.latest : '',
    versions,
  }
}

// Client-level read used by callers that already hold an S3Client. Missing
// and corrupt indexes both resolve to null — a broken versions-index must
// degrade to the folder-listing fallback, never take the whole toy (or a
// whole index rebuild) down with it.
export async function getVersionsIndex(
  client: S3Client,
  identityId: string,
  toyId: string,
): Promise<VersionsIndex | null> {
  const result = await getJsonObject(client, UPLOAD_BUCKET, versionsIndexKey(identityId, toyId))
  if (result.kind === 'missing') return null
  if (result.kind === 'invalid-json') {
    console.warn('[toy-versions] skipping malformed versions-index.json', {
      toyId,
      err: result.error,
    })
    Sentry.captureException(result.error, { extra: { identityId, toyId } })
    return null
  }
  const index = result.kind === 'ok' ? parseVersionsIndex(result.value) : null
  if (!index) {
    console.warn('[toy-versions] skipping versions-index.json with bad shape', { toyId })
    Sentry.captureMessage('versions-index.json has invalid shape', {
      level: 'warning',
      extra: { identityId, toyId },
    })
  }
  return index
}

export async function putVersionsIndex(
  client: S3Client,
  identityId: string,
  toyId: string,
  index: VersionsIndex,
): Promise<void> {
  const body = new TextEncoder().encode(JSON.stringify(index, null, 2))
  await client.send(
    new PutObjectCommand({
      Bucket: UPLOAD_BUCKET,
      Key: versionsIndexKey(identityId, toyId),
      Body: body,
      ContentType: 'application/json',
    }),
  )
}

// Lists the semver-named version folders directly under a toy. This is the
// on-disk source of truth the versions-index is a cache of, mirroring how
// toy-index.json relates to the toy folders themselves.
export async function listVersionFolders(
  client: S3Client,
  identityId: string,
  toyId: string,
): Promise<string[]> {
  const prefix = `${identityId}/${toyId}/`
  const versions: string[] = []
  for await (const res of paginateList(client, {
    Bucket: UPLOAD_BUCKET,
    Prefix: prefix,
    Delimiter: '/',
  })) {
    for (const p of res.CommonPrefixes ?? []) {
      if (!p.Prefix) continue
      const name = p.Prefix.slice(prefix.length).replace(/\/$/, '')
      if (isSemver(name)) versions.push(name)
    }
  }
  return versions
}

export async function loadVersionsIndex(
  io: S3Io,
  identityId: string,
  toyId: string,
): Promise<VersionsIndex | null> {
  return withS3Client(io, (client) => getVersionsIndex(client, identityId, toyId))
}

// Plain unconditional write. Fine for the current single-writer contexts (a
// creation writing the index for its own fresh UUID, a lone admin migrating);
// revisit with conditional writes if multiple writers ever share a toy.
export async function saveVersionsIndex(
  io: S3Io,
  identityId: string,
  toyId: string,
  index: VersionsIndex,
): Promise<void> {
  await withS3Client(io, (client) => putVersionsIndex(client, identityId, toyId, index))
}
